import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import EmployeeService from '../../../services/EmployeeService';
import { AuthContext } from '../../../context/AuthContext';

const AddEmployee = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Employee data state matching the schema
  const [employeeData, setEmployeeData] = useState({
    employeeId: '',
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      maritalStatus: 'Single',
      nationality: 'Indian',
      aadharNumber: '',
      panNumber: ''
    },
    address: {
      current: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      permanent: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      }
    },
    emergencyContact: {
      name: '',
      phone: '',
      relation: '',
      address: ''
    },
    department: '',
    position: '',
    joinedDate: '',
    salary: {
      bankDetails: {
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: '',
        accountHolderName: ''
      }
    }
  });

  const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Department options with corresponding positions
  const departmentOptions = {
    IT: ['Developer', 'System Administrator', 'IT Support', 'DevOps Engineer', 'QA Engineer'],
    HR: ['HR Manager', 'HR Assistant', 'Recruiter', 'Training Coordinator'],
    Finance: ['Accountant', 'Financial Analyst', 'Finance Manager', 'Payroll Specialist'],
    Marketing: ['Marketing Executive', 'SEO Specialist', 'Content Creator', 'Brand Manager'],
    Operations: ['Operations Manager', 'Project Coordinator', 'Business Analyst'],
    Sales: ['Sales Executive', 'Sales Manager', 'Account Manager']
  };

  // Handle nested state updates
  const handleNestedChange = (section, field, value, subsection = null) => {
    setEmployeeData(prev => {
      const newData = { ...prev };
      if (subsection) {
        newData[section][subsection][field] = value;
      } else {
        newData[section][field] = value;
      }
      return newData;
    });
  };

  // Handle direct field updates
  const handleDirectChange = (field, value) => {
    setEmployeeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Copy current address to permanent
  const handleAddressCopy = (checked) => {
    setSameAsCurrentAddress(checked);
    if (checked) {
      setEmployeeData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          permanent: { ...prev.address.current }
        }
      }));
    }
  };

  // Generate Employee ID
  const generateEmployeeId = () => {
    const dept = employeeData.department.substring(0, 3).toUpperCase();
    const year = new Date().getFullYear().toString().slice(-2); // Last two digits of the year
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0'); // Month in MM format
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `KKGT-${dept}-${year}${month}${random}`;
  };

  // Validate form data
  const validateForm = () => {
    const requiredFields = {
      'Employee ID': employeeData.employeeId,
      'First Name': employeeData.personalInfo.firstName,
      'Last Name': employeeData.personalInfo.lastName,
      'Email': employeeData.personalInfo.email,
      'Phone': employeeData.personalInfo.phone,
      'Date of Birth': employeeData.personalInfo.dateOfBirth,
      'Gender': employeeData.personalInfo.gender,
      'Department': employeeData.department,
      'Position': employeeData.position,
      'Joined Date': employeeData.joinedDate,
      'Emergency Contact Name': employeeData.emergencyContact.name,
      'Emergency Contact Phone': employeeData.emergencyContact.phone,
      'Emergency Contact Relation': employeeData.emergencyContact.relation,
      'Account Number': employeeData.salary.bankDetails.accountNumber,
      'IFSC Code': employeeData.salary.bankDetails.ifscCode,
      'Bank Name': employeeData.salary.bankDetails.bankName,
      'Account Holder Name': employeeData.salary.bankDetails.accountHolderName
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => key);

    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(employeeData.personalInfo.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Validate phone format
    const phoneRegex = /^[\+]?[1-9][\d]{9,15}$/;
    if (!phoneRegex.test(employeeData.personalInfo.phone.replace(/\s/g, ''))) {
      setError('Please enter a valid phone number');
      return false;
    }

    // Validate Aadhar if provided
    if (employeeData.personalInfo.aadharNumber && 
        !/^\d{12}$/.test(employeeData.personalInfo.aadharNumber)) {
      setError('Aadhar number must be exactly 12 digits');
      return false;
    }

    // Validate PAN if provided
    if (employeeData.personalInfo.panNumber && 
        !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(employeeData.personalInfo.panNumber)) {
      setError('PAN number must be in format: ABCDE1234F');
      return false;
    }

    // Validate IFSC Code
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(employeeData.salary.bankDetails.ifscCode)) {
      setError('IFSC code must be in format: ABCD0123456');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await EmployeeService.createEmployee(employeeData);
      setLoading(false);
      navigate('/dashboard/employee-list');
    } catch (err) {
      console.error('Error creating employee:', err);
      setError(err.response?.data?.error || 'Failed to create employee. Please try again.');
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'personal', label: 'Personal Details' },
    { id: 'address', label: 'Address' },
    { id: 'emergency', label: 'Emergency Contact' },
    { id: 'employment', label: 'Employment' },
    { id: 'bank', label: 'Bank Details' }
  ];

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Add New Employee</h1>
        <p className="text-gray-600 mt-2">Fill in the employee information below</p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={employeeData.personalInfo.firstName}
                onChange={(e) => handleNestedChange('personalInfo', 'firstName', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John"
                required
              />
            </div>
            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={employeeData.personalInfo.lastName}
                onChange={(e) => handleNestedChange('personalInfo', 'lastName', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Doe"
                required
              />
            </div>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={employeeData.personalInfo.email}
                onChange={(e) => handleNestedChange('personalInfo', 'email', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="john.doe@example.com"
                required
              />
            </div>
            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                value={employeeData.personalInfo.phone}
                onChange={(e) => handleNestedChange('personalInfo', 'phone', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+91 9876543210"
                required
              />
            </div>
            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth *
              </label>
              <input
                type="date"
                value={employeeData.personalInfo.dateOfBirth}
                onChange={(e) => handleNestedChange('personalInfo', 'dateOfBirth', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            {/* Department (move here for Employee ID generation) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department *
              </label>
              <select
                value={employeeData.department}
                onChange={(e) => {
                  handleDirectChange('department', e.target.value);
                  handleDirectChange('position', '');
                  // Clear Employee ID if department changes
                  handleDirectChange('employeeId', '');
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Department</option>
                {Object.keys(departmentOptions).map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            {/* Employee ID (after department selection) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee ID *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={employeeData.employeeId}
                  onChange={(e) => handleDirectChange('employeeId', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="EMP001"
                  required
                  disabled={!employeeData.department}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (employeeData.department) {
                      handleDirectChange('employeeId', generateEmployeeId());
                    }
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm"
                  disabled={!employeeData.department}
                >
                  Generate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Personal Details Tab */}
        {activeTab === 'personal' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender *
              </label>
              <select
                value={employeeData.personalInfo.gender}
                onChange={(e) => handleNestedChange('personalInfo', 'gender', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marital Status
              </label>
              <select
                value={employeeData.personalInfo.maritalStatus}
                onChange={(e) => handleNestedChange('personalInfo', 'maritalStatus', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nationality
              </label>
              <input
                type="text"
                value={employeeData.personalInfo.nationality}
                onChange={(e) => handleNestedChange('personalInfo', 'nationality', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Indian"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aadhar Number
              </label>
              <input
                type="text"
                value={employeeData.personalInfo.aadharNumber}
                onChange={(e) => handleNestedChange('personalInfo', 'aadharNumber', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123456789012"
                maxLength="12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PAN Number
              </label>
              <input
                type="text"
                value={employeeData.personalInfo.panNumber}
                onChange={(e) => handleNestedChange('personalInfo', 'panNumber', e.target.value.toUpperCase())}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ABCDE1234F"
                maxLength="10"
              />
            </div>
          </div>
        )}

        {/* Address Tab */}
        {activeTab === 'address' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Current Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.current.street}
                    onChange={(e) => handleNestedChange('address', 'street', e.target.value, 'current')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.current.city}
                    onChange={(e) => handleNestedChange('address', 'city', e.target.value, 'current')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Mumbai"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.current.state}
                    onChange={(e) => handleNestedChange('address', 'state', e.target.value, 'current')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Maharashtra"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.current.pincode}
                    onChange={(e) => handleNestedChange('address', 'pincode', e.target.value, 'current')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="400001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.current.country}
                    onChange={(e) => handleNestedChange('address', 'country', e.target.value, 'current')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="India"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="sameAddress"
                  checked={sameAsCurrentAddress}
                  onChange={(e) => handleAddressCopy(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="sameAddress" className="text-sm text-gray-700">
                  Permanent address same as current address
                </label>
              </div>

              <h3 className="text-lg font-semibold text-gray-700 mb-4">Permanent Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.permanent.street}
                    onChange={(e) => handleNestedChange('address', 'street', e.target.value, 'permanent')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street"
                    disabled={sameAsCurrentAddress}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.permanent.city}
                    onChange={(e) => handleNestedChange('address', 'city', e.target.value, 'permanent')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Mumbai"
                    disabled={sameAsCurrentAddress}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.permanent.state}
                    onChange={(e) => handleNestedChange('address', 'state', e.target.value, 'permanent')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Maharashtra"
                    disabled={sameAsCurrentAddress}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.permanent.pincode}
                    onChange={(e) => handleNestedChange('address', 'pincode', e.target.value, 'permanent')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="400001"
                    disabled={sameAsCurrentAddress}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={employeeData.address.permanent.country}
                    onChange={(e) => handleNestedChange('address', 'country', e.target.value, 'permanent')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="India"
                    disabled={sameAsCurrentAddress}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Emergency Contact Tab */}
        {activeTab === 'emergency' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Name *
              </label>
              <input
                type="text"
                value={employeeData.emergencyContact.name}
                onChange={(e) => handleNestedChange('emergencyContact', 'name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Emergency contact name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Phone *
              </label>
              <input
                type="tel"
                value={employeeData.emergencyContact.phone}
                onChange={(e) => handleNestedChange('emergencyContact', 'phone', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+91 9876543210"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Relationship *
              </label>
              <input
                type="text"
                value={employeeData.emergencyContact.relation}
                onChange={(e) => handleNestedChange('emergencyContact', 'relation', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Father/Mother/Spouse"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                value={employeeData.emergencyContact.address}
                onChange={(e) => handleNestedChange('emergencyContact', 'address', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Emergency contact address"
              />
            </div>
          </div>
        )}

        {/* Employment Tab */}
        {activeTab === 'employment' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Remove Department field from here, since it's now in Basic Info tab */}
            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position *
              </label>
              <select
                value={employeeData.position}
                onChange={(e) => handleDirectChange('position', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!employeeData.department}
                required
              >
                <option value="">Select Position</option>
                {employeeData.department && departmentOptions[employeeData.department]?.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Joining Date *
              </label>
              <input
                type="date"
                value={employeeData.joinedDate}
                onChange={(e) => handleDirectChange('joinedDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        )}

        {/* Bank Details Tab */}
        {activeTab === 'bank' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Number *
              </label>
              <input
                type="text"
                value={employeeData.salary.bankDetails.accountNumber}
                onChange={(e) => handleNestedChange('salary', 'accountNumber', e.target.value, 'bankDetails')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1234567890"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IFSC Code *
              </label>
              <input
                type="text"
                value={employeeData.salary.bankDetails.ifscCode}
                onChange={(e) => handleNestedChange('salary', 'ifscCode', e.target.value.toUpperCase(), 'bankDetails')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ABCD0123456"
                maxLength="11"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bank Name *
              </label>
              <input
                type="text"
                value={employeeData.salary.bankDetails.bankName}
                onChange={(e) => handleNestedChange('salary', 'bankName', e.target.value, 'bankDetails')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="State Bank of India"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch Name
              </label>
              <input
                type="text"
                value={employeeData.salary.bankDetails.branchName}
                onChange={(e) => handleNestedChange('salary', 'branchName', e.target.value, 'bankDetails')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Main Branch"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Holder Name *
              </label>
              <input
                type="text"
                value={employeeData.salary.bankDetails.accountHolderName}
                onChange={(e) => handleNestedChange('salary', 'accountHolderName', e.target.value, 'bankDetails')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
                required
              />
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="mt-8 flex justify-between items-center border-t pt-6">
          <button
            type="button"
            onClick={() => navigate('/dashboard/employee-list')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>

          <div className="flex gap-3">
            {activeTab !== 'basic' && (
              <button
                type="button"
                onClick={() => {
                  const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                  if (currentIndex > 0) {
                    setActiveTab(tabs[currentIndex - 1].id);
                  }
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Previous
              </button>
            )}

            {activeTab !== 'bank' ? (
              <button
                type="button"
                onClick={() => {
                  const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                  if (currentIndex < tabs.length - 1) {
                    setActiveTab(tabs[currentIndex + 1].id);
                  }
                }}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Employee...' : 'Create Employee'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEmployee;